// RUTA: src/components/support/TicketCreatedDialog.tsx
//
// Modal de confirmación que aparece después de crear un ticket de
// soporte. Es el patrón "verifica que el usuario vio el mensaje" —
// le mostramos el número del ticket y le decimos qué pasa después.
//
// Reusable en cualquier parte del sistema que cree un ticket.

import { CheckCircle2, LifeBuoy, Clock } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { slaForPriority, type TicketPriority } from '@/lib/supportSla';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Número del ticket creado (ej. "IFT-042"). */
  ticketNumber: string | null;
  /** Prioridad del ticket. Si se pasa, mostramos el SLA esperado para
   *  que el cliente sepa cuánto vamos a tardar en responderle.
   *  P3/P4 reciben acuse automático inmediato; P1/P2 esperamos
   *  que respondamos rápido manualmente. */
  priority?: TicketPriority | null;
  /** Callback al hacer click en "Ver mis tickets". Opcional. Si no se
   *  pasa, el botón no aparece. */
  onViewTickets?: () => void;
}

export function TicketCreatedDialog({ open, onOpenChange, ticketNumber, priority, onViewTickets }: Props) {
  const sla = priority ? slaForPriority(priority) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center justify-center mb-3">
            <div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center ${
              priority === 'P1'
                ? 'bg-destructive/10 border-destructive/40'
                : 'bg-green-500/10 border-green-500/30'
            }`}>
              <CheckCircle2 className={`w-8 h-8 ${
                priority === 'P1' ? 'text-destructive' : 'text-green-600 dark:text-green-400'
              }`} />
            </div>
          </div>
          <DialogTitle className="text-center text-base font-black uppercase tracking-wide">
            {priority === 'P1' ? 'Reporte urgente recibido' : 'Reporte recibido'}
          </DialogTitle>
          <DialogDescription className="text-center text-sm space-y-3 pt-2">
            <span className="block">
              {priority === 'P1'
                ? 'Detectamos que esto es crítico. Estamos al tanto y respondemos lo antes posible.'
                : 'Tu reporte llegó al equipo de soporte y ya está en nuestra cola.'
              }
            </span>

            {ticketNumber && (
              <span className="block px-4 py-3 rounded-xl bg-secondary border-2 border-border">
                <span className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                  Tu número de reporte
                </span>
                <span className="block text-xl font-black tracking-wider text-accent">
                  {ticketNumber}
                </span>
              </span>
            )}

            {sla && (
              <span className="block px-3 py-2.5 rounded-lg bg-accent/5 border border-accent/30">
                <span className="flex items-center justify-center gap-1.5 text-xs font-bold text-accent">
                  <Clock className="w-3.5 h-3.5" />
                  Te respondemos en máximo {sla.label}
                </span>
                {(priority === 'P3' || priority === 'P4') && (
                  <span className="block text-[10px] text-muted-foreground mt-1">
                    Mientras tanto, tu app sigue funcionando normal.
                  </span>
                )}
              </span>
            )}

            <span className="block text-xs text-muted-foreground">
              Puedes ver el estado en cualquier momento desde el botón <span className="font-bold inline-flex items-center gap-1"><LifeBuoy className="w-3 h-3" /> Ayuda</span> abajo a la derecha.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {onViewTickets && (
            <Button
              variant="outline"
              onClick={() => { onViewTickets(); onOpenChange(false); }}
              className="w-full sm:w-auto"
            >
              Ver mis tickets
            </Button>
          )}
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            Aceptar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
